
import { supabase } from '@/integrations/supabase/client';
import { Profile } from './types';

// Fetch user profile data from Supabase
export const fetchUserProfile = async (userId: string): Promise<Profile | null> => {
  try {
    // First get the basic profile
    const { data: profileData, error: profileError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (profileError) {
      console.error('Error fetching user profile:', profileError);
      return null;
    }

    // Get user favorites
    const { data: favoritesData } = await supabase
      .from('favorites')
      .select('terrace_id')
      .eq('user_id', userId);
    
    // Get recently viewed terraces
    const { data: recentlyViewedData } = await supabase
      .from('recently_viewed')
      .select('terrace_id')
      .eq('user_id', userId)
      .order('viewed_at', { ascending: false })
      .limit(5);

    // Get user stats
    const { data: statsData } = await supabase
      .from('user_stats')
      .select('*')
      .eq('user_id', userId)
      .single();

    // Combine all data into the profile structure that matches our type definition
    const completeProfile: Profile = {
      ...profileData,
      favorites: favoritesData?.map(f => f.terrace_id) || [],
      recentlyViewed: recentlyViewedData?.map(r => r.terrace_id) || [],
      notificationPreferences: {
        favoriteTerracesSunny: true,
        weeklyDigest: true,
        specialOffers: false
      },
      sunnyStats: statsData ? {
        hoursThisWeek: statsData.hours_this_week || 0,
        hoursThisMonth: statsData.hours_this_month || 0,
        favoriteTerrace: statsData.favorite_terrace_id || null,
        visitCount: statsData.visit_count || 0
      } : {
        hoursThisWeek: 0,
        hoursThisMonth: 0,
        favoriteTerrace: null,
        visitCount: 0
      }
    };

    return completeProfile;
  } catch (error) {
    console.error('Exception fetching user profile:', error);
    return null;
  }
};

// Fetch user role from user_roles table
export const fetchUserRole = async (userId: string): Promise<string | null> => {
  try {
    const { data, error } = await supabase
      .rpc('get_user_role', { _user_id: userId });

    if (error) {
      console.error('Error fetching user role:', error);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Exception fetching user role:', error);
    return null;
  }
};
